import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Users, IdCard, HeartPulse, Dumbbell, ClipboardCheck,
  Euro, BookOpen, FolderOpen, Megaphone, ShieldCheck, Building2, LogOut,
  Menu, X, UserCircle, Smartphone, MapPin,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, exact: true, always: true },
  { to: "/app/portale", label: "Portale", icon: Smartphone, roles: ["atleta", "genitore"] },
  { to: "/app/anagrafica", label: "Anagrafica", icon: Users, mod: "anagrafica" },
  { to: "/app/tesseramenti", label: "Tesseramenti", icon: IdCard, mod: "tesseramenti" },
  { to: "/app/certificati", label: "Certificati Medici", icon: HeartPulse, mod: "certificati" },
  { to: "/app/corsi", label: "Corsi e Squadre", icon: Dumbbell, mod: "corsi" },
  { to: "/app/presenze", label: "Presenze", icon: ClipboardCheck, mod: "presenze" },
  { to: "/app/impianti", label: "Gestione Impianti", icon: MapPin, mod: "impianti" },
  { to: "/app/pagamenti", label: "Quote e Pagamenti", icon: Euro, mod: "pagamenti" },
  { to: "/app/contabilita", label: "Cassa e Prima Nota", icon: BookOpen, mod: "contabilita" },
  { to: "/app/documenti", label: "Documenti", icon: FolderOpen, mod: "documenti" },
  { to: "/app/comunicazioni", label: "Comunicazioni", icon: Megaphone, mod: "comunicazioni" },
  { to: "/app/utenti", label: "Utenti e Ruoli", icon: ShieldCheck, mod: "utenti" },
  { to: "/app/piattaforma", label: "Piattaforma ASD", icon: Building2, roles: ["super_admin"] },
];

export default function AppLayout() {
  const { user, tenant, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const visible = NAV.filter((n) => {
    if (n.always) return true;
    if (n.roles) return n.roles.includes(user?.role);
    if (n.mod) return can(n.mod, "view");
    return false;
  });

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-800/60">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-sky-500 grid place-items-center text-white font-bold font-display">A</div>
          <div>
            <p className="text-white font-bold font-display leading-tight">ASD Manager</p>
            <p className="text-[11px] text-slate-400">Cloud Platform</p>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 border-b border-slate-800/60">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ambiente</p>
        <p className="text-sm text-slate-200 font-semibold truncate">{tenant?.name || "Piattaforma"}</p>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {visible.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.exact} onClick={() => setOpen(false)}
            data-testid={`nav-${n.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-sky-500/15 text-sky-300" : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
              }`}>
            <n.icon size={18} />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800/60">
        <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
          <UserCircle size={30} className="text-slate-400" />
          <div className="min-w-0">
            <p className="text-sm text-white font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.role_label}</p>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={doLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white">
          <LogOut size={16} /> Esci
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col fixed inset-y-0 z-30">{SidebarContent()}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-slate-900 flex flex-col">{SidebarContent()}</aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu size={22} /></button>
          <span className="font-bold font-display">{tenant?.name || "ASD Manager"}</span>
          <span className="w-6" />
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
